import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { GraduationCapIcon, UsersIcon, CalendarIcon, ArrowRightIcon, SearchIcon, GlobeIcon, BriefcaseIcon, BuildingIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, FileTextIcon, BarChart3Icon, TrendingUpIcon, ChevronDownIcon, DownloadIcon } from 'lucide-react';
const MoHESRDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [approvalFilter, setApprovalFilter] = useState('all');
  // Sample data for pending approvals
  const pendingApprovals = [{
    id: 'ps001',
    title: 'ADNOC Future Engineers',
    employer: 'ADNOC',
    program: 'Petroleum Engineering',
    preferredUniversity: 'Imperial College London',
    submissionDate: '2023-06-10',
    status: 'pending'
  }, {
    id: 'ps002',
    title: 'Etisalat Digital Innovators',
    employer: 'Etisalat',
    program: 'Computer Science',
    preferredUniversity: 'Multiple Options',
    submissionDate: '2023-06-08',
    status: 'pending'
  }, {
    id: 'ps003',
    title: 'Emirates Aviation Leaders',
    employer: 'Emirates',
    program: 'Aviation Management',
    preferredUniversity: 'Embry-Riddle Aeronautical University',
    submissionDate: '2023-06-05',
    status: 'pending'
  }];
  // Sample data for applications
  const applications = [{
    id: 'app001',
    studentName: 'Ahmed Al Mansouri',
    track: 'Elite',
    program: 'Computer Science at MIT',
    status: 'review',
    submissionDate: '2023-06-15'
  }, {
    id: 'app002',
    studentName: 'Fatima Al Zaabi',
    track: 'Partnership',
    program: 'Petroleum Engineering (ADNOC)',
    status: 'interview',
    submissionDate: '2023-06-12'
  }, {
    id: 'app003',
    studentName: 'Mohammed Al Hashimi',
    track: 'Elite',
    program: 'Medicine at Harvard',
    status: 'review',
    submissionDate: '2023-06-10'
  }, {
    id: 'app004',
    studentName: 'Sara Al Dhaheri',
    track: 'Partnership',
    program: 'Digital Innovation (Etisalat)',
    status: 'forwarded',
    submissionDate: '2023-06-08'
  }];
  // Helper function to get status color
  const getStatusColor = status => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'review':
        return 'bg-blue-100 text-blue-800';
      case 'interview':
        return 'bg-purple-100 text-purple-800';
      case 'forwarded':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Format date for display
  const formatDate = dateString => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  return <Layout title="Dashboard Overview" persona="MoHESR Admin" data-id="element-2940">
      <div className="max-w-7xl mx-auto" data-id="element-2941">
        {/* Stats cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-id="element-2942">
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-2943">
            <div className="flex items-start justify-between" data-id="element-2944">
              <div data-id="element-2945">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-2946">
                  Elite Track Scholarships
                </h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-2947">15</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2948">
                  Active scholarships
                </p>
              </div>
              <div className="p-3 bg-primary-50 rounded-full" data-id="element-2949">
                <GraduationCapIcon className="w-6 h-6 text-primary-500" data-id="element-2950" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-2951">
            <div className="flex items-start justify-between" data-id="element-2952">
              <div data-id="element-2953">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-2954">
                  Partnership Scholarships
                </h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-2955">28</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2956">From 12 employers</p>
              </div>
              <div className="p-3 bg-primary-50 rounded-full" data-id="element-2957">
                <BriefcaseIcon className="w-6 h-6 text-primary-500" data-id="element-2958" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-2959">
            <div className="flex items-start justify-between" data-id="element-2960">
              <div data-id="element-2961">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-2962">
                  Pending Approvals
                </h3>
                <div className="text-3xl font-bold text-yellow-500" data-id="element-2963">3</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2964">
                  Require your review
                </p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-full" data-id="element-2965">
                <AlertCircleIcon className="w-6 h-6 text-yellow-500" data-id="element-2966" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-2967">
            <div className="flex items-start justify-between" data-id="element-2968">
              <div data-id="element-2969">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-2970">Applications</h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-2971">42</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2972">
                  8 requiring decision
                </p>
              </div>
              <div className="p-3 bg-primary-50 rounded-full" data-id="element-2973">
                <FileTextIcon className="w-6 h-6 text-primary-500" data-id="element-2974" />
              </div>
            </div>
          </div>
        </div>
        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2975">
          {/* Pending Partnership Approvals */}
          <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-2976">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-2977">
              <h2 className="text-lg font-medium text-gray-800" data-id="element-2978">
                Pending Partnership Approvals
              </h2>
              <button onClick={() => navigate('/mohesr/partnership-approvals')} className="text-sm text-primary-500 hover:underline flex items-center" data-id="element-2979">
                View All
                <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-2980" />
              </button>
            </div>
            <div className="overflow-x-auto" data-id="element-2981">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-2982">
                <thead className="bg-gray-50" data-id="element-2983">
                  <tr data-id="element-2984">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2985">
                      Scholarship
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2986">
                      Employer
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2987">
                      Program
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2988">
                      Submitted
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2989">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-2990">
                  {pendingApprovals.map(approval => <tr key={approval.id} className="hover:bg-gray-50" data-id="element-2991">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2992">
                        <div className="text-sm font-medium text-gray-900" data-id="element-2993">
                          {approval.title}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2994">
                        <div className="text-sm text-gray-500" data-id="element-2995">
                          {approval.employer}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2996">
                        <div className="text-sm text-gray-500" data-id="element-2997">
                          {approval.program}
                        </div>
                        <div className="text-xs text-gray-400" data-id="element-2998">
                          {approval.preferredUniversity}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2999">
                        {formatDate(approval.submissionDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-3000">
                        <div className="flex space-x-2" data-id="element-3001">
                          <button className="text-green-500 hover:text-green-700" onClick={() => navigate(`/mohesr/partnership-approvals/${approval.id}`)} data-id="element-3002">
                            <CheckCircleIcon className="w-5 h-5" data-id="element-3003" />
                          </button>
                          <button className="text-red-500 hover:text-red-700" data-id="element-3004">
                            <XCircleIcon className="w-5 h-5" data-id="element-3005" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
          {/* Quick Actions */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-3006">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-3007">
              <h2 className="text-lg font-medium text-gray-800" data-id="element-3008">
                Quick Actions
              </h2>
            </div>
            <div className="p-6 space-y-4" data-id="element-3009">
              <button onClick={() => navigate('/mohesr/elite-track')} className="w-full bg-primary-500 hover:bg-primary-600 text-white py-3 px-4 rounded-md font-medium flex items-center justify-between" data-id="element-3010">
                <div className="flex items-center" data-id="element-3011">
                  <GraduationCapIcon className="w-5 h-5 mr-3" data-id="element-3012" />
                  Manage Elite Track
                </div>
                <ArrowRightIcon className="w-5 h-5" data-id="element-3013" />
              </button>
              <button onClick={() => navigate('/mohesr/eligible-lists')} className="w-full bg-secondary-600 hover:bg-secondary-700 text-white py-3 px-4 rounded-md font-medium flex items-center justify-between" data-id="element-3014">
                <div className="flex items-center" data-id="element-3015">
                  <GlobeIcon className="w-5 h-5 mr-3" data-id="element-3016" />
                  Update Eligible Universities
                </div>
                <ArrowRightIcon className="w-5 h-5" data-id="element-3017" />
              </button>
              <button onClick={() => navigate('/mohesr/applications')} className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-md font-medium flex items-center justify-between" data-id="element-3018">
                <div className="flex items-center" data-id="element-3019">
                  <FileTextIcon className="w-5 h-5 mr-3" data-id="element-3020" />
                  Review Applications
                </div>
                <ArrowRightIcon className="w-5 h-5" data-id="element-3021" />
              </button>
              <button onClick={() => navigate('/mohesr/partnership-approvals')} className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-3 px-4 rounded-md font-medium flex items-center justify-between" data-id="element-3022">
                <div className="flex items-center" data-id="element-3023">
                  <BriefcaseIcon className="w-5 h-5 mr-3" data-id="element-3024" />
                  Partnership Approvals
                </div>
                <ArrowRightIcon className="w-5 h-5" data-id="element-3025" />
              </button>
            </div>
          </div>
        </div>
        {/* Applications Requiring Decision */}
        <div className="mt-6 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-3026">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-3027">
            <h2 className="text-lg font-medium text-gray-800" data-id="element-3028">
              Applications Requiring Decision
            </h2>
            <button onClick={() => navigate('/mohesr/applications')} className="text-sm text-primary-500 hover:underline flex items-center" data-id="element-3029">
              View All Applications
              <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-3030" />
            </button>
          </div>
          <div className="overflow-x-auto" data-id="element-3031">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-3032">
              <thead className="bg-gray-50" data-id="element-3033">
                <tr data-id="element-3034">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3035">
                    Student
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3036">
                    Track
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3037">
                    Program
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3038">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3039">
                    Submitted
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3040">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-3041">
                {applications.map(application => <tr key={application.id} className="hover:bg-gray-50" data-id="element-3042">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3043">
                      <div className="text-sm font-medium text-gray-900" data-id="element-3044">
                        {application.studentName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3045">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${application.track === 'Elite' ? 'bg-primary-100 text-primary-800' : 'bg-secondary-100 text-secondary-800'}`} data-id="element-3046">
                        {application.track}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3047">
                      <div className="text-sm text-gray-500" data-id="element-3048">
                        {application.program}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3049">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(application.status)}`} data-id="element-3050">
                        {application.status === 'review' ? 'Under Review' : application.status === 'interview' ? 'Interview Stage' : application.status === 'forwarded' ? 'Forwarded to MoHESR' : application.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3051">
                      {formatDate(application.submissionDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-3052">
                      <button className="text-primary-500 hover:text-primary-700" onClick={() => navigate(`/mohesr/applications/${application.id}`)} data-id="element-3053">
                        Review
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
        {/* Analytics Overview */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3054">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-3055">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-3056">
              <h2 className="text-lg font-medium text-gray-800" data-id="element-3057">
                Scholarship Distribution
              </h2>
              <button className="text-sm text-gray-500 hover:text-gray-700" data-id="element-3058">
                <DownloadIcon className="w-4 h-4" data-id="element-3059" />
              </button>
            </div>
            <div className="p-6" data-id="element-3060">
              <div className="h-64 flex items-center justify-center" data-id="element-3061">
                <div className="text-center text-gray-500" data-id="element-3062">
                  <BarChart3Icon className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-3063" />
                  <p data-id="element-3064">Analytics chart would appear here</p>
                  <p className="text-sm mt-2" data-id="element-3065">
                    Showing distribution of scholarships by field of study
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-3066">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-3067">
              <h2 className="text-lg font-medium text-gray-800" data-id="element-3068">
                Application Trends
              </h2>
              <button className="text-sm text-gray-500 hover:text-gray-700" data-id="element-3069">
                <DownloadIcon className="w-4 h-4" data-id="element-3070" />
              </button>
            </div>
            <div className="p-6" data-id="element-3071">
              <div className="h-64 flex items-center justify-center" data-id="element-3072">
                <div className="text-center text-gray-500" data-id="element-3073">
                  <TrendingUpIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-3074" />
                  <p data-id="element-3075">Analytics chart would appear here</p>
                  <p className="text-sm mt-2" data-id="element-3076">
                    Showing application trends over time
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>;
};
export default MoHESRDashboard;